import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { api, unwrap, type CaseSummary } from "../api/client";

/** The analysis profile a case's recordings use, over their library's (design §8.4). */
export function CaseAnalysis({ c }: { c: CaseSummary }) {
  const qc = useQueryClient();
  const key = ["case-analysis", c.id];
  const analysis = useQuery({
    queryKey: key,
    queryFn: () =>
      unwrap(
        api.GET("/api/cases/{id}/analysis", { params: { path: { id: c.id } } }),
      ),
  });
  const set = useMutation({
    mutationFn: (profile_id: string | null) =>
      unwrap(
        api.PUT("/api/cases/{id}/analysis", {
          params: { path: { id: c.id } },
          body: { profile_id },
        }),
      ),
    onSuccess: (data) => qc.setQueryData(key, data),
  });
  const rerun = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/api/cases/{id}/analysis/rerun", {
          params: { path: { id: c.id } },
          body: { kinds: ["speech", "motion"] },
        }),
      ),
  });
  const may = c.permissions.includes("RunAnalytics");
  const data = analysis.data;
  if (!data || (data.profiles.length === 0 && !data.profile_id)) return null;
  const current = data.profiles.find((p) => p.id === data.profile_id);

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2 }}
      component="section"
      aria-label="Analysis"
    >
      <Typography variant="h6" component="h3" gutterBottom>
        Analysis
      </Typography>
      <Stack
        direction="row"
        sx={{ gap: 2, alignItems: "center", flexWrap: "wrap" }}
      >
        {may ? (
          <TextField
            size="small"
            select
            label="Analysis profile"
            value={data.profile_id ?? ""}
            onChange={(e) => set.mutate(e.target.value || null)}
            disabled={set.isPending}
            sx={{ minWidth: 240 }}
            slotProps={{ select: { displayEmpty: true } }}
          >
            <MenuItem value="">Each recording&apos;s library&apos;s</MenuItem>
            {data.profiles.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <Typography>
            {current
              ? `Analysed with the profile ${current.name}`
              : "Each recording is analysed with its library's profile."}
          </Typography>
        )}
        {may && (
          <Button
            size="small"
            disabled={rerun.isPending}
            onClick={() => {
              if (
                window.confirm(
                  `Analyse every recording in ${c.name} again? This can take a while; earlier results are kept.`,
                )
              )
                rerun.mutate();
            }}
          >
            Run analysis again
          </Button>
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        A recording in several cases with profiles uses the one chosen most
        recently. New settings apply to analysis run from now on.
      </Typography>
      {set.isError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {set.error.message}
        </Alert>
      )}
      {rerun.isError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {rerun.error.message}
        </Alert>
      )}
      {rerun.isSuccess && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Analysis queued again for {rerun.data.queued} recordings.
        </Alert>
      )}
    </Paper>
  );
}
